import { useCallback, useEffect, useMemo } from 'react'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'
import isEqual from 'lodash/isEqual'

import IconTitleGroup from 'common/components/iconTitleGroup'
import { Topic } from 'common/components/topics/interfaces'
import { useDeepCompare } from 'common/hooks/custom'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { Add } from 'common/icons'

import TopicCard from './components/TopicCard'
import { useTopicHandlers } from './hooks/useTopicHandlers'
import { loadError, updateContentId, updateTopics } from './store/topicsSlice'

const SUBJECT_LINE_LENGTH_THRESHOLD = 100
const MAX_CONSTANTS_LENGTH = 2

export type FocusThemesPickerProps = {
  topics: Topic[]
  isDisabled?: boolean
  onChange: (topics: Topic[]) => void
  className?: string
  contentId?: string
}

const FocusThemesPicker = ({
  topics: initialTopics,
  isDisabled,
  onChange,
  className,
  contentId,
}: FocusThemesPickerProps) => {
  const dispatch = useAppDispatch()
  const { byPassTheLimitFocusTheme, addFocusTheme } = useFlags()

  const {
    editingTopicId,
    isAddingNewTopic,
    newTopicName,
    setNewTopicName,
    handleAddEmptyTopic,
    handleNewTopicSubmit,
    handleNewTopicKeyDown,
    handleEditClick,
    handleNameChange,
  } = useTopicHandlers()

  const currentTopics = useAppSelector((state) => state.topics.topics)
  const contentStructure = useAppSelector(
    (state) => state.campaignStates.combinedLanguageSettings.contentStructure
  )

  const subjectLineLengthConfig = contentStructure?.find(
    (item) => item.name === 'Subject Line'
  )?.length
  const maxConstantsLength =
    subjectLineLengthConfig &&
    subjectLineLengthConfig < SUBJECT_LINE_LENGTH_THRESHOLD
      ? 1
      : byPassTheLimitFocusTheme?.bypass
        ? byPassTheLimitFocusTheme.number
        : MAX_CONSTANTS_LENGTH

  const constants = useMemo(
    () => currentTopics.filter((topic) => topic.isConstant),
    [currentTopics]
  )

  const hasInitialTopicsChanged = useDeepCompare(initialTopics)

  useEffect(() => {
    if (hasInitialTopicsChanged) {
      dispatch(updateTopics(initialTopics))
    } else if (!isEqual(currentTopics, initialTopics)) {
      onChange(currentTopics)
    }
  }, [
    currentTopics,
    dispatch,
    hasInitialTopicsChanged,
    initialTopics,
    onChange,
  ])

  useEffect(() => {
    if (contentId) {
      dispatch(updateContentId(contentId))
    }
  }, [contentId, dispatch])

  useEffect(() => {
    dispatch(loadError(false))
  }, [dispatch])

  const renderAddInput = useCallback(
    () => (
      <input
        type="text"
        value={newTopicName}
        onChange={(e) => setNewTopicName(e.target.value)}
        onKeyDown={handleNewTopicKeyDown}
        onBlur={handleNewTopicSubmit}
        placeholder="Enter focus theme"
        className="px-3 py-2 border-none outline-none bg-transparent text-base font-normal min-w-40"
        autoFocus
      />
    ),
    [newTopicName, setNewTopicName, handleNewTopicKeyDown, handleNewTopicSubmit]
  )

  return (
    <div className={cx('relative overflow-hidden', className)}>
      <div className="mb-4">
        <IconTitleGroup title="Focus Themes" />
      </div>
      <div
        data-testid="focused-topics-list"
        className="flex flex-wrap gap-4 p-4 min-h-24 border border-gold-400 relative rounded-sm items-center"
      >
        <div
          className="absolute text-base-700 text-base font-medium opacity-50
          top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        >
          {constants.length === 0
            ? isAddingNewTopic && addFocusTheme
              ? renderAddInput()
              : addFocusTheme && (
                  <button
                    onClick={handleAddEmptyTopic}
                    className="underline hover:text-base-900 cursor-pointer"
                    disabled={isDisabled}
                    data-testid="add-focus-theme-button-text"
                  >
                    Add a focus theme
                  </button>
                )
            : null}
        </div>
        {constants.map((constant) => (
          <TopicCard
            topic={constant}
            key={constant.id}
            isDisabled={isDisabled}
            style={{}}
            isDragged
            isEditing={addFocusTheme ? editingTopicId === constant.id : false}
            onEditClick={
              addFocusTheme ? () => handleEditClick(constant.id) : undefined
            }
            onNameChange={
              addFocusTheme
                ? (newName) => handleNameChange(constant.id, newName)
                : undefined
            }
          />
        ))}
        {constants.length < maxConstantsLength &&
          constants.length > 0 &&
          (isAddingNewTopic && addFocusTheme ? (
            renderAddInput()
          ) : addFocusTheme ? (
            <button
              onClick={handleAddEmptyTopic}
              className="hover:text-base-900 cursor-pointer flex items-center gap-1"
              disabled={isDisabled}
              data-testid="add-focus-theme-button"
            >
              <Add size={4} isDefaultColor={false} />
              Add
            </button>
          ) : null)}
      </div>
    </div>
  )
}

export default FocusThemesPicker
